import {
  CheckboxFieldStateType,
  RadioFieldStateType,
  DropdownFieldStateType,
  FieldsType,
  InputFieldStateType,
} from 'common/types/fields';

import { OptionType } from 'common/components/Select/types';
import { FallbackFormValues, StringKeys } from 'utils/type-tools';
import { FieldPath } from 'react-hook-form';
import { IToFormAdapters, IToServiceAdapters, RecordUnionValueType } from './types';
import { ISchemaDataStorage, ISchemaDataStorageFields } from '../GenericFieldsHandlingLayer/types';


export const getUpdatedFieldToForm = <TField,
    TFormValues extends FallbackFormValues = FallbackFormValues>(

    field: RecordUnionValueType<ISchemaDataStorage<FieldPath<TFormValues>>['fields']>,
    toForm: IToFormAdapters<TField>,
  ) => {
  if (!field) {
    return undefined;
  }
  switch (field.type) {
    case FieldsType.input: {
      return toForm.inputToFormAdapter!(field);
    }
    case FieldsType.textarea: {
      return toForm.inputToFormAdapter!(field);
    }
    case FieldsType.dropdown: {
      return toForm.dropdownToFormAdapter!(field);
    }
    case FieldsType.checkboxes: {
      return toForm.checkboxToFormAdapter!(field);
    }
    case FieldsType.radioGroup: {
      return toForm.radioToFormAdapter!(field);
    }
    default:
      return undefined;
  }
};

export const getUpdatedFieldToService = <TFormValues extends FallbackFormValues = FallbackFormValues>(
  fieldType: FieldsType,
  fieldName: StringKeys<TFormValues>,
  storedField: RecordUnionValueType<ISchemaDataStorageFields<StringKeys<TFormValues>>>,
  toService: IToServiceAdapters<StringKeys<TFormValues>>,
  fieldVal?: TFormValues[keyof TFormValues],
) => {
  switch (fieldType) {
    case FieldsType.textarea:
    case FieldsType.input: {
      return toService.inputToServiceAdapter!(
        fieldName,
        fieldVal as unknown as string,
      );
    }
    case FieldsType.dropdown: {
      return toService.dropdownToServiceAdapter!(
        fieldName,
        (storedField as DropdownFieldStateType).value!.options,
        fieldVal as unknown as OptionType,
      );
    }
    case FieldsType.checkboxes: {
      return toService.checkboxToServiceAdapter!(
        fieldName,
        (storedField as CheckboxFieldStateType).value!.options,
        fieldVal as unknown as Array<string>,
      );
    }
    case FieldsType.radioGroup: {
      return toService.radioToServiceAdapter!(
        fieldName,
        (storedField as RadioFieldStateType).value!.options,
        fieldVal as unknown as string,
      );
    }
    default:
      return undefined;
  }
};

export const getStoredFieldToService = <TFormValues extends FallbackFormValues = FallbackFormValues>(
  storedField: RecordUnionValueType<ISchemaDataStorageFields<StringKeys<TFormValues>>>,
  toService: IToServiceAdapters<StringKeys<TFormValues>>,
) => {
  if (!storedField) {
    return undefined;
  }
  switch (storedField!.type) {
    case FieldsType.textarea:
    case FieldsType.input: {
      return toService.inputToServiceAdapter!(
        storedField.name as StringKeys<TFormValues>,
        (storedField as InputFieldStateType).value?.selected?.value,
      );
    }
    case FieldsType.dropdown: {
      const selected = (storedField as DropdownFieldStateType).value?.selected; // TODO: backend returns not an array atm
      return toService.dropdownToServiceAdapter!(
        storedField.name as StringKeys<TFormValues>,
        (storedField as DropdownFieldStateType).value?.options,
        (selected && selected.length) ? selected[0] : undefined,
      );
    }
    case FieldsType.checkboxes: {
      const selected = (storedField as CheckboxFieldStateType).value?.selected;
      return toService.checkboxToServiceAdapter!(
        storedField.name as StringKeys<TFormValues>,
        (storedField as CheckboxFieldStateType).value?.options,
        selected ? selected.map((s) => s.id) : undefined,
      );
    }
    case FieldsType.radioGroup: {
      const selected = (storedField as RadioFieldStateType).value?.selected;
      return toService.radioToServiceAdapter!(
        storedField.name as StringKeys<TFormValues>,
        (storedField as RadioFieldStateType).value?.options,
        selected ? selected[0].id : undefined,
      );
    }
    default:
      return undefined;
  }
};
